import type { CodeSystem, StructureDefinition, ValueSet } from '@medplum/fhirtypes';
import clinicalCaseProcedure from './dist/fsh-generated/resources/StructureDefinition-clinical-case-procedure.json' with { type: 'json' };
import codeModifier from './dist/fsh-generated/resources/StructureDefinition-code-modifier.json' with { type: 'json' };
import confidence from './dist/fsh-generated/resources/StructureDefinition-confidence.json' with { type: 'json' };
import sourceNoteDocumentReference from './dist/fsh-generated/resources/StructureDefinition-source-note-document-reference.json' with { type: 'json' };
import procedurePerformerRole from './dist/fsh-generated/resources/CodeSystem-procedure-performer-role.json' with { type: 'json' };
import sourceKind from './dist/fsh-generated/resources/CodeSystem-source-kind.json' with { type: 'json' };
import procedurePerformerRoleValueSet from './dist/fsh-generated/resources/ValueSet-procedure-performer-role-vs.json' with { type: 'json' };
import sourceKindValueSet from './dist/fsh-generated/resources/ValueSet-source-kind-vs.json' with { type: 'json' };

type CanonicalResource = CodeSystem | StructureDefinition | ValueSet;

const asCodeSystem = (resource: unknown) => resource as CodeSystem;
const asStructureDefinition = (resource: unknown) => resource as StructureDefinition;
const asValueSet = (resource: unknown) => resource as ValueSet;

const toCamelCase = (value: string) => {
  return value.replaceAll(/[-_]+([a-z0-9])/gi, (_, character: string) => character.toUpperCase());
};

const createUrlMap = <T extends Record<string, CanonicalResource>>(resources: T) => {
  return Object.fromEntries(
    Object.entries(resources).map(([key, resource]) => {
      if (!resource.url) {
        throw new Error(`Expected ${key} to define a canonical URL.`);
      }

      return [key, resource.url];
    }),
  ) as { [K in keyof T]: string };
};

const createCodeMap = (codeSystem: CodeSystem) => {
  return Object.fromEntries(
    (codeSystem.concept ?? []).map((concept) => {
      if (!concept.code) {
        throw new Error(`Expected ${codeSystem.id ?? 'code system'} concept to define a code.`);
      }

      return [toCamelCase(concept.code), concept.code];
    }),
  );
};

export const panelHealthExtensionResources = {
  codeModifier: asStructureDefinition(codeModifier),
  confidence: asStructureDefinition(confidence),
} as const satisfies Record<string, StructureDefinition>;

export const panelHealthExtensionUrls = {
  ...createUrlMap(panelHealthExtensionResources),
  approach: 'http://hl7.org/fhir/StructureDefinition/procedure-method',
} as const;

export const panelHealthCodeSystemResources = {
  procedurePerformerRole: asCodeSystem(procedurePerformerRole),
  sourceKind: asCodeSystem(sourceKind),
} as const satisfies Record<string, CodeSystem>;

export const panelHealthCodeSystems = createUrlMap(panelHealthCodeSystemResources);

const panelHealthValueSetResources = {
  procedurePerformerRole: asValueSet(procedurePerformerRoleValueSet),
  sourceKind: asValueSet(sourceKindValueSet),
} satisfies Record<string, ValueSet>;

const panelHealthProfiles = {
  clinicalCaseProcedure: asStructureDefinition(clinicalCaseProcedure),
  sourceNoteDocumentReference: asStructureDefinition(sourceNoteDocumentReference),
} as const satisfies Record<string, StructureDefinition>;

export const panelHealthProfileUrls = createUrlMap(panelHealthProfiles);

export const panelHealthPractitionerTypes = {
  attending: 'attending',
  resident: 'resident',
} as const;

export type PanelHealthPractitionerType =
  (typeof panelHealthPractitionerTypes)[keyof typeof panelHealthPractitionerTypes];

export const panelHealthAccessPolicyNames = {
  [panelHealthPractitionerTypes.attending]: 'Panel Health Attending',
  [panelHealthPractitionerTypes.resident]: 'Panel Health Resident',
} as const satisfies Record<PanelHealthPractitionerType, string>;

export const procedurePerformerRoleCodes = createCodeMap(
  panelHealthCodeSystemResources.procedurePerformerRole,
);
export const sourceKindCodes = createCodeMap(panelHealthCodeSystemResources.sourceKind);

export const panelHealthStructureDefinitions = [
  ...Object.values(panelHealthExtensionResources),
  ...Object.values(panelHealthProfiles),
] as const;

export const panelHealthFhirResources = [
  ...Object.values(panelHealthCodeSystemResources),
  ...Object.values(panelHealthValueSetResources),
  ...panelHealthStructureDefinitions,
] as const;
